/**
 * Performance Cache Utility for Dashboard
 * Extracted from Dashboard.js - advanced caching system for large datasets
 */

export class DashboardCache {
  constructor(options = {}) {
    this.cache = new Map();
    this.cacheTimeout = options.timeout || 2 * 60 * 1000; // 2 minutes default
    this.maxSize = options.maxSize || 50; // Maximum cache entries
    this.debugMode = options.debugMode || false;
  }
  
  /**
   * Generate cache key from filters
   */
  getCacheKey(filters) {
    const parts = [
      filters.period,
      filters.niche || 'all',
      filters.customer || 'all',
      filters.startDate || '',
      filters.endDate || ''
    ];
    return parts.join('-');
  }
  
  /**
   * Get cached data
   */
  get(filters) {
    const key = this.getCacheKey(filters);
    const cached = this.cache.get(key);
    
    if (cached && (Date.now() - cached.timestamp) < this.cacheTimeout) {
      if (this.debugMode) {
      }
      return cached.data;
    }
    
    // Clean expired entries while we're here
    this._cleanExpired();
    return null;
  }
  
  /**
   * Set cached data
   */
  set(filters, data) {
    const key = this.getCacheKey(filters);
    
    // Ensure we don't exceed max size
    if (this.cache.size >= this.maxSize) {
      this._evictOldest();
    }
    
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
    
    if (this.debugMode) {
    }
  }
  
  /**
   * Clear all cache
   */
  clear() {
    this.cache.clear();
    if (this.debugMode) {
    }
  }
  
  /**
   * Get cache statistics
   */
  getStats() {
    const now = Date.now();
    const entries = Array.from(this.cache.entries());
    const active = entries.filter(([_, value]) => (now - value.timestamp) < this.cacheTimeout);
    
    return {
      size: this.cache.size,
      activeEntries: active.length,
      expiredEntries: this.cache.size - active.length,
      keys: Array.from(this.cache.keys()).map(k => k.substring(0, 30) + '...'),
      memoryUsage: this._estimateMemoryUsage()
    };
  }
  
  /**
   * Clean expired entries
   */
  _cleanExpired() {
    const now = Date.now();
    const keysToDelete = [];
    
    for (const [key, value] of this.cache.entries()) {
      if ((now - value.timestamp) >= this.cacheTimeout) {
        keysToDelete.push(key);
      }
    }
    
    keysToDelete.forEach(key => {
      this.cache.delete(key);
      if (this.debugMode) {
      }
    });
  }
  
  /**
   * Evict oldest entry when cache is full
   */
  _evictOldest() {
    let oldestKey = null;
    let oldestTime = Date.now();
    
    for (const [key, value] of this.cache.entries()) {
      if (value.timestamp < oldestTime) {
        oldestTime = value.timestamp;
        oldestKey = key;
      }
    }
    
    if (oldestKey) {
      this.cache.delete(oldestKey);
      if (this.debugMode) {
      }
    }
  }
  
  /**
   * Estimate memory usage (rough calculation)
   */
  _estimateMemoryUsage() {
    let totalSize = 0;
    
    for (const [key, value] of this.cache.entries()) {
      // Rough estimate: key + timestamp + data size
      totalSize += key.length * 2; // UTF-16
      totalSize += 8; // timestamp
      totalSize += this._estimateObjectSize(value.data);
    }
    
    return {
      bytes: totalSize,
      kb: Math.round(totalSize / 1024),
      mb: Math.round(totalSize / (1024 * 1024))
    };
  }
  
  /**
   * Rough object size estimation
   */
  _estimateObjectSize(obj) {
    if (obj === null || obj === undefined) return 0;
    if (typeof obj === 'string') return obj.length * 2;
    if (typeof obj === 'number') return 8;
    if (typeof obj === 'boolean') return 4;
    if (Array.isArray(obj)) {
      return obj.reduce((size, item) => size + this._estimateObjectSize(item), 0);
    }
    if (typeof obj === 'object') {
      return Object.keys(obj).reduce((size, key) => {
        return size + key.length * 2 + this._estimateObjectSize(obj[key]);
      }, 0);
    }
    return 0;
  }
  
  /**
   * Warm cache with common queries
   */
  async warmCache(commonFilters = []) {
    if (this.debugMode) {
    }
    
    // This would be implemented by the calling code
    // Just marking the intention here
    return Promise.resolve();
  }
  
  /**
   * Export cache state (for debugging/monitoring)
   */
  exportState() {
    const state = {
      entries: Array.from(this.cache.entries()).map(([key, value]) => ({
        key,
        timestamp: value.timestamp,
        age: Date.now() - value.timestamp,
        expired: (Date.now() - value.timestamp) >= this.cacheTimeout,
        dataSize: this._estimateObjectSize(value.data)
      })),
      stats: this.getStats(),
      config: {
        timeout: this.cacheTimeout,
        maxSize: this.maxSize,
        debugMode: this.debugMode
      }
    };
    
    return state;
  }
}

/**
 * Performance Monitor for tracking dashboard performance
 */
export class PerformanceMonitor {
  constructor() {
    this.metrics = new Map();
  }
  
  startTimer(operation) {
    const startTime = performance.now();
    return {
      end: () => {
        const endTime = performance.now();
        const duration = endTime - startTime;
        this.recordMetric(operation, duration);
        return duration;
      }
    };
  }
  
  recordMetric(operation, duration, metadata = {}) {
    if (!this.metrics.has(operation)) {
      this.metrics.set(operation, []);
    }
    
    this.metrics.get(operation).push({
      duration,
      timestamp: Date.now(),
      ...metadata
    });
    
    // Keep only last 100 measurements per operation
    const measurements = this.metrics.get(operation);
    if (measurements.length > 100) {
      measurements.splice(0, measurements.length - 100);
    }
  }
  
  getStats(operation = null) {
    if (operation) {
      const measurements = this.metrics.get(operation) || [];
      return this._calculateStats(measurements);
    }
    
    const allStats = {};
    for (const [op, measurements] of this.metrics.entries()) {
      allStats[op] = this._calculateStats(measurements);
    }
    return allStats;
  }
  
  _calculateStats(measurements) {
    if (measurements.length === 0) {
      return { count: 0, avg: 0, min: 0, max: 0, latest: 0 };
    }
    
    const durations = measurements.map(m => m.duration);
    const sum = durations.reduce((a, b) => a + b, 0);
    
    return {
      count: measurements.length,
      avg: sum / measurements.length,
      min: Math.min(...durations),
      max: Math.max(...durations),
      latest: durations[durations.length - 1],
      p95: this._percentile(durations, 0.95),
      trend: this._calculateTrend(durations)
    };
  }
  
  _percentile(values, percentile) {
    const sorted = values.slice().sort((a, b) => a - b);
    const index = Math.ceil(sorted.length * percentile) - 1;
    return sorted[index] || 0;
  }
  
  _calculateTrend(values) {
    if (values.length < 2) return 'stable';
    
    const recent = values.slice(-10); // Last 10 measurements
    const older = values.slice(-20, -10); // Previous 10 measurements
    
    if (recent.length === 0 || older.length === 0) return 'stable';
    
    const recentAvg = recent.reduce((a, b) => a + b, 0) / recent.length;
    const olderAvg = older.reduce((a, b) => a + b, 0) / older.length;
    
    const change = (recentAvg - olderAvg) / olderAvg;
    
    if (change > 0.2) return 'degrading';
    if (change < -0.2) return 'improving';
    return 'stable';
  }
  
  clearMetrics() {
    this.metrics.clear();
  }
}

// Global instances
export const dashboardCache = new DashboardCache({
  timeout: 2 * 60 * 1000, // 2 minutes
  maxSize: 50,
  debugMode: process.env.NODE_ENV === 'development'
});

// Separate cache for trend data with longer TTL (10 minutes)
// Historical trend data rarely changes, only recent days get updated
export const trendCache = new DashboardCache({
  timeout: 10 * 60 * 1000, // 10 minutes
  maxSize: 20,
  debugMode: process.env.NODE_ENV === 'development'
});

export const performanceMonitor = new PerformanceMonitor();

// Utility function for cache-aware data loading
export async function withCache(cacheKey, dataLoader, cache = dashboardCache) {
  // Try cache first
  const cached = cache.get(cacheKey);
  if (cached) {
    return { ...cached, _fromCache: true };
  }
  
  // Load fresh data
  const timer = performanceMonitor.startTimer('dataLoad');
  try {
    const data = await dataLoader();
    const duration = timer.end();
    
    // Cache the result
    cache.set(cacheKey, data);
    
    return { 
      ...data, 
      _fromCache: false, 
      _loadTime: duration,
      _timestamp: Date.now()
    };
  } catch (error) {
    timer.end();
    throw error;
  }
}
